import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { BottomNav } from "../components/chalk/primitives";
import AddToHomeScreenPrompt from "../components/chalk/AddToHomeScreenPrompt";
import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { startCheckout } from "../lib/checkout";

const ACTIVE_BY_PATH = {
  "/app": "board",
  "/app/playbook": "playbook",
  "/app/roster": "roster",
  "/app/family": "family",
  "/app/talk": "talk",
  "/app/more": "more",
};

const SHELL_STYLE = { background: "var(--chalk-bg)" };
const INNER_STYLE = { maxWidth: 480, margin: "0 auto" };

const INTENT_KEY = "chalk_wants_checkout";
const PAID_TIERS = new Set(["clipboard_plus", "chalkboard", "league_os"]);

export default function AppShell() {
  const { user } = useAuth();
  const { activeTeam } = useTeam();
  const location = useLocation();
  const variant = user?.role === "guardian" ? "parent" : "coach";
  let activeTab = ACTIVE_BY_PATH[location.pathname] || "board";
  if (variant === "parent" && activeTab === "board") activeTab = "clipboard";
  const hideNav = location.pathname.startsWith("/admin");

  const outletContext = useMemo(() => ({ team: activeTeam, user }), [activeTeam, user]);
  const shellStyle = useMemo(
    () => ({ ...SHELL_STYLE, paddingBottom: hideNav ? 0 : 80 }),
    [hideNav]
  );

  return (
    <div className="chalk-app-shell" style={shellStyle}>
      <AutoCheckoutRedirect />
      <div style={INNER_STYLE}>
        <Outlet context={outletContext} />
      </div>
      {!hideNav && <BottomNav variant={variant} activeTab={activeTab} />}
      <AddToHomeScreenPrompt />
    </div>
  );
}

/**
 * Post-signup auto-checkout funnel.
 *
 * Reads the `chalk_wants_checkout` intent set by Login.jsx after signup
 * (coach → chalkboard, guardian → clipboard_plus) and redirects the first-time
 * user straight into Stripe Checkout when they land on /app. One-shot:
 * the intent is cleared on success, skip, or error.
 *
 * Guards:
 *  • Skip if user is already on a paid/trialing tier.
 *  • Skip while teams are still resolving (avoid empty team_id payloads).
 *  • Cleared on any failure so the user is never trapped in a redirect loop.
 */
function AutoCheckoutRedirect() {
  const { user } = useAuth();
  const { activeTeamId, loading: teamsLoading } = useTeam();
  const [redirecting, setRedirecting] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!user) return;
    if (teamsLoading) return;

    let intent = null;
    try { intent = sessionStorage.getItem(INTENT_KEY); } catch { return; }
    if (intent !== "chalkboard" && intent !== "clipboard_plus") return;

    // Don't auto-redirect parents who never want to upgrade — make it explicit
    // by only firing for the matching role to be safe.
    const role = user.role;
    if (intent === "chalkboard" && !(role === "coach" || role === "league_admin")) return;
    if (intent === "clipboard_plus" && role !== "guardian") return;

    firedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        // Skip if already on a paid/trialing plan.
        const sub = await api.mySubscription().catch(() => null);
        const cur = sub && sub.subscription;
        if (cur && PAID_TIERS.has(cur.tier) && (cur.status === "active" || cur.status === "trialing")) {
          clearIntent();
          return;
        }

        if (cancelled) return;

        // Clear before redirect so a back-button bounce doesn't re-trigger.
        clearIntent();
        setRedirecting(true);
        await startCheckout({
          tier: intent,
          teamId: intent === "chalkboard" ? activeTeamId || undefined : undefined,
        });
        // browser navigates away on success
      } catch (e) {
        if (cancelled) return;
        setRedirecting(false);
        clearIntent();
        // Signup already succeeded by the time we get here. A checkout failure
        // shouldn't paint a scary red toast on top of a fresh account — drop
        // a friendly informational toast instead and let the user keep going.
        if (e?.code === "STRIPE_PRICE_MISSING" || e?.code === "STRIPE_NOT_CONFIGURED") {
          toast.info("Upgrade isn't available right now — you're all set on the free tier. You can upgrade later from More.");
        } else {
          toast.info("You can upgrade anytime from More.");
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user, activeTeamId, teamsLoading]);

  if (!redirecting) return null;
  return <CheckoutOverlay onSkip={() => setRedirecting(false)} />;
}

function clearIntent() {
  try { sessionStorage.removeItem(INTENT_KEY); } catch { /* ignore */ }
}

function CheckoutOverlay({ onSkip }) {
  return (
    <div
      data-testid="auto-checkout-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        className="chalk-card"
        style={{
          background: "var(--chalk-white)", maxWidth: 360, width: "100%",
          padding: "26px 22px", textAlign: "center", borderRadius: 18,
        }}
      >
        <div className="chalk-spinner" aria-hidden="true" style={{
          width: 28, height: 28, margin: "0 auto 14px",
          border: "2.5px solid rgba(0,0,0,0.08)", borderTopColor: "var(--chalk-green)",
          borderRadius: "50%", animation: "chalk-spin 0.9s linear infinite",
        }} />
        <div className="font-serif-display" style={{ fontSize: 20, color: "var(--chalk-board)" }}>
          Opening secure checkout…
        </div>
        <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 6 }}>
          You're being redirected to Stripe.
        </div>
        <button
          type="button"
          data-testid="auto-checkout-skip"
          onClick={onSkip}
          style={{
            marginTop: 18, background: "transparent", border: "none",
            color: "var(--chalk-muted)", fontSize: 12, cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Skip for now
        </button>
      </div>
      <style>{`@keyframes chalk-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
